import { useQuery } from "@tanstack/react-query";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { fillTemplate, type TemplateVars } from "../templates/fillTemplate";

export type ScriptSection = { title: string; body: string };
export type CallScript = { templateId: number; sections: ScriptSection[] };

/** "## Título\ntexto…" (una o más secciones) → [{title, body}], en orden. */
const parseSections = (md: string): ScriptSection[] =>
  md
    .split(/\n(?=##\s+)/)
    .map((block) => block.match(/^##\s+([^\n]+)\n?([\s\S]*)$/))
    .filter((m): m is RegExpMatchArray => !!m && !!m[2].trim())
    .map((m) => ({ title: m[1].trim(), body: m[2].trim() }));

/**
 * Guion de llamada (Bloque T.6): la variante que gane pick_template para
 * (oferta, llamada, d1_llamada) — misma regla que usan los envíos y la
 * cadencia, en vez del archivo fijo playbook.ts.
 * ponytail: siempre d1_llamada (el guion fijo tampoco variaba por número de
 * intento, solo por producto); step_key por intento si algún día se necesita
 * medir cada llamada de la cadencia por separado.
 */
export const useCallScript = (
  offerId: number | null,
  vars: TemplateVars,
): CallScript | null | undefined =>
  useQuery({
    queryKey: ["call_script", offerId],
    enabled: !!offerId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<CallScript | null> => {
      const sb = getSupabaseClient();
      const { data: id } = await sb.rpc("pick_template", {
        p_offer_id: offerId!,
        p_channel: "llamada",
        p_step_key: "d1_llamada",
      });
      if (!id) return null;
      const { data: t } = await sb
        .from("templates")
        .select("id, body")
        .eq("id", id)
        .single();
      if (!t) return null;
      const sections = parseSections(fillTemplate(t.body, vars));
      return sections.length ? { templateId: t.id, sections } : null;
    },
  }).data;
